import type {TocEntry} from '@github-ui/code-view-types'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {FilterIcon, XIcon} from '@primer/octicons-react'
import {IconButton, NavList, TextInput} from '@primer/react'
import React, {useCallback, useEffect, useDeferredValue, useMemo, useRef, useState} from 'react'

import {onHashChange} from './SharedMarkdownContent'

import styles from './TableOfContentsPanel.module.css'

// CSS class lookup for level-based indentation (avoids creating style objects)
const levelClasses: Record<number, string> = {
  1: styles.TocLevel1,
  2: styles.TocLevel2,
  3: styles.TocLevel3,
  4: styles.TocLevel4,
  5: styles.TocLevel5,
  6: styles.TocLevel6,
}

interface TocItemProps extends Pick<TocEntry, 'anchor' | 'htmlText' | 'level'> {
  hash: string
  index: number
}

// Memoized list item to avoid re-renders when filter/hash doesn't affect this item
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
const TocItem = React.memo(function TocItem({htmlText, anchor, level, hash, index}: TocItemProps) {
  const hashString = `#${anchor}`
  const isCurrent = hash === hashString
  // Clamp level to valid range (1-6) to handle any unexpected values
  const levelClass = levelClasses[Math.max(1, Math.min(6, level))]

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.button === 1 || e.metaKey || e.ctrlKey) {
        return
      }
      if (!isCurrent) {
        location.href = hashString
      }
      onHashChange(hashString)
      e.preventDefault()
    },
    [hashString, isCurrent],
  )

  return (
    <NavList.Item
      key={`outline-${anchor}-${index}`}
      aria-current={isCurrent ? 'page' : undefined}
      href={hashString}
      onClick={handleClick}
    >
      <VerifiedHTMLDiv className={levelClass} html={htmlText} />
    </NavList.Item>
  )
})

export default function TableOfContentsPanel({onClose, toc}: {onClose?: () => void; toc: TocEntry[] | null}) {
  const [filter, setFilter] = useState<string>('')
  // Defer filter to keep typing responsive while filtering happens in background
  const deferredFilter = useDeferredValue(filter)
  const [hash, setHash] = useState<string>('')
  const headingRef = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  useEffect(() => {
    const hashChange = () => {
      if (window.location.hash) {
        setHash(window.location.hash)
      }
    }

    hashChange()
    window.addEventListener('hashchange', hashChange)
    return () => {
      window.removeEventListener('hashchange', hashChange)
    }
  }, [])

  // Filter TOC using deferred value so input stays responsive
  const filteredToc = useMemo(() => {
    if (!toc) return []
    if (!deferredFilter) return toc
    const lowerFilter = deferredFilter.toLowerCase()
    return toc.filter(({htmlText}) => htmlText && htmlText.toLowerCase().includes(lowerFilter))
  }, [toc, deferredFilter])

  if (!toc) {
    return null
  }

  return (
    <section aria-labelledby="outline-id" className={styles.Box}>
      {onClose ? (
        <div className="d-flex flex-justify-between flex-items-center">
          <h3
            id="outline-id"
            ref={headingRef}
            className="d-flex flex-justify-between flex-items-center f5 text-bold px-2"
            tabIndex={-1}
          >
            Outline
          </h3>
          <IconButton
            aria-label="Close outline"
            tooltipDirection="sw"
            className="fgColor-muted"
            icon={XIcon}
            onClick={onClose}
            variant="invisible"
          />
        </div>
      ) : null}
      {toc.length >= 8 ? (
        <div className="tmp-pt-3 px-2">
          <TextInput
            leadingVisual={FilterIcon}
            placeholder="Filter headings"
            aria-label="Filter headings"
            className="width-full"
            onChange={e => {
              setFilter(e.target.value)
            }}
          />
        </div>
      ) : null}
      <NavList className={styles.NavList}>
        {filteredToc.map((entry, index) => (
          <TocItem
            key={`outline-${entry.anchor}-${index}`}
            anchor={entry.anchor}
            htmlText={entry.htmlText}
            level={entry.level}
            hash={hash}
            index={index}
          />
        ))}
      </NavList>
    </section>
  )
}
